import React from 'react'
import { Helmet } from 'react-helmet-async'
import {
  environmentConfig,
  getEnvironmentMetaTags,
} from '../../config/environment'
import { GLOBAL_SEO, getCanonicalUrl, getPageSEO } from '../../data/seo'
import type { PageSEOConfig } from '../../types/seo'

/**
 * SEOHead component provides comprehensive SEO meta tag management
 * for Build the Stack portfolio using react-helmet-async
 *
 * Features:
 * - Dynamic page titles and descriptions
 * - Open Graph meta tags for social media
 * - Twitter Card optimization
 * - Structured data (JSON-LD) injection
 * - Canonical URL management
 * - Search engine indexing control
 * - Environment-aware robot indexing (automatic noindex for staging)
 */

interface SEOHeadProps {
  /** Page identifier to load SEO config */
  page: string
  /** Custom SEO configuration override */
  config?: Partial<PageSEOConfig>
  /** Current page path for canonical URL */
  path?: string
}

/**
 * Main SEO Head component
 */
export const SEOHead: React.FC<SEOHeadProps> = ({
  page,
  config,
  path = '/',
}) => {
  // Get page configuration directly without dynamic import
  const pageConfig = getPageSEO(page)

  // Don't render if no config found
  if (!pageConfig) {
    console.warn(`No SEO config found for page: ${page}`)
    return null
  }

  // Merge default config with custom overrides
  const seoConfig: PageSEOConfig = {
    seo: { ...pageConfig.seo, ...config?.seo },
    openGraph: { ...pageConfig.openGraph, ...config?.openGraph },
    twitterCard: { ...pageConfig.twitterCard, ...config?.twitterCard },
    structuredData: config?.structuredData || pageConfig.structuredData,
  }

  // Environment-based robot indexing override
  const environmentNoIndex = !environmentConfig.enableSeoIndexing
  const shouldNoIndex = environmentNoIndex || seoConfig.seo.noindex

  // Generate canonical URL
  const canonicalUrl = seoConfig.seo.canonical || getCanonicalUrl(path)

  // Get environment-specific meta tags
  const environmentMetaTags = getEnvironmentMetaTags()

  const { seo, openGraph, twitterCard, structuredData } = seoConfig

  return (
    <Helmet prioritizeSeoTags>
      {/* Basic Meta Tags */}
      <title>{seo.title}</title>
      <meta name="description" content={seo.description} />
      {seo.keywords && (
        <meta name="keywords" content={seo.keywords.join(', ')} />
      )}
      <meta name="author" content={GLOBAL_SEO.author} />
      <link rel="canonical" href={canonicalUrl} />

      {/* Environment-Aware Robots Meta Tags */}
      {environmentMetaTags.map((tag, index) => (
        <meta key={index} name={tag.name} content={tag.content} />
      ))}

      {/* Additional robot directives if not handled by environment */}
      {!environmentNoIndex && seo.nofollow && (
        <meta name="robots" content="index, nofollow" />
      )}

      {/* Open Graph Meta Tags */}
      <meta property="og:type" content={openGraph.type} />
      <meta property="og:title" content={openGraph.title} />
      <meta property="og:description" content={openGraph.description} />
      <meta property="og:url" content={openGraph.url} />
      <meta property="og:image" content={openGraph.image} />
      <meta property="og:image:alt" content={openGraph.imageAlt} />
      <meta property="og:site_name" content={openGraph.siteName} />
      <meta
        property="og:locale"
        content={openGraph.locale || GLOBAL_SEO.locale}
      />

      {/* Twitter Card Meta Tags */}
      <meta name="twitter:card" content={twitterCard.card} />
      <meta name="twitter:title" content={twitterCard.title} />
      <meta name="twitter:description" content={twitterCard.description} />
      <meta name="twitter:image" content={twitterCard.image} />
      <meta name="twitter:image:alt" content={twitterCard.imageAlt} />
      {twitterCard.creator && (
        <meta name="twitter:creator" content={twitterCard.creator} />
      )}
      {twitterCard.site && (
        <meta name="twitter:site" content={twitterCard.site} />
      )}

      {/* Additional Meta Tags */}
      <meta name="theme-color" content={GLOBAL_SEO.themeColor} />
      <meta name="format-detection" content="telephone=no" />

      {/* Structured Data - Only include if indexing is enabled */}
      {!shouldNoIndex &&
        structuredData?.map((schema, index) => (
          <script
            key={`structured-data-${page}-${index}`}
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify(schema, null, 0),
            }}
          />
        ))}
    </Helmet>
  )
}

/**
 * Hook for updating page SEO dynamically
 */
export const useSEO = (
  page: string,
  config?: Partial<PageSEOConfig>,
  path?: string
) => {
  return React.useMemo(
    () => ({
      SEOComponent: () => <SEOHead page={page} config={config} path={path} />,
    }),
    [page, config, path]
  )
}

export default SEOHead
